import React, { useState, useEffect, useRef, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Terminal, Clock, Star } from 'lucide-react';
export interface Command {
  id: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  category?: string;
  keywords?: string[];
  action: () => void;
  shortcut?: string;
}

export interface CommandPaletteProps {
  commands: Command[];
  isOpen: boolean;
  onClose: () => void;
  recentCommands?: string[];
  className?: string;
}

export function CommandPalette({
  commands,
  isOpen,
  onClose,
  recentCommands = [],
  className = '',
}: CommandPaletteProps) {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter and rank commands
  const filteredCommands = React.useMemo(() => {
    if (!search) {
      // Show recent commands first when no search
      const recent = recentCommands
        .map(id => commands.find(cmd => cmd.id === id))
        .filter(Boolean) as Command[];
      const others = commands.filter(cmd => !recentCommands.includes(cmd.id));
      return [...recent, ...others];
    }

    const searchLower = search.toLowerCase();
    return commands
      .map(cmd => {
        let score = 0;
        const labelLower = cmd.label.toLowerCase();
        const descLower = cmd.description?.toLowerCase() || '';

        // Exact match bonus
        if (labelLower === searchLower) score += 100;
        
        // Starts with bonus
        if (labelLower.startsWith(searchLower)) score += 50;
        
        // Contains in label
        if (labelLower.includes(searchLower)) score += 25;
        
        // Contains in description
        if (descLower.includes(searchLower)) score += 10;
        
        // Keyword match
        if (cmd.keywords?.some(kw => kw.toLowerCase().includes(searchLower))) score += 15;

        return { cmd, score };
      })
      .filter(({ score }) => score > 0)
      .sort((a, b) => b.score - a.score)
      .map(({ cmd }) => cmd);
  }, [search, commands, recentCommands]);

  // Group commands by category
  const groupedCommands = React.useMemo(() => {
    const groups = new Map<string, Command[]>();
    filteredCommands.forEach(cmd => {
      const category = cmd.category || 'Other';
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category)!.push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  // Reset selection when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
            onClose();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 modal-backdrop"
        />

        {/* Command Palette */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ type: 'spring', duration: 0.3 }}
          className={`relative w-full max-w-2xl bg-khiimori-bg-elevated rounded-xl shadow-2xl border border-khiimori-border overflow-hidden ${className}`}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-khiimori-border">
            <Search className="w-5 h-5 text-khiimori-text-muted" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Type a command or search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-khiimori-text-primary text-khiimori-text-primary placeholder-gray-400 text-base"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-mono text-khiimori-text-muted bg-khiimori-bg-elevated rounded">
              ESC
            </kbd>
          </div>

          {/* Commands List */}
          <div className="max-h-[60vh] overflow-y-auto">
            {filteredCommands.length === 0 ? (
              <div className="p-8 text-center text-khiimori-text-muted">
                <Terminal className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No commands found</p>
                <p className="text-sm mt-1">Try a different search term</p>
              </div>
            ) : (
              <div className="py-2">
                {!search && recentCommands.length > 0 && (
                  <div className="px-3 py-2">
                    <div className="flex items-center gap-2 text-xs font-semibold text-khiimori-text-muted uppercase tracking-wider mb-2">
                      <Clock className="w-4 h-4" />
                      Recent
                    </div>
                  </div>
                )}

                {Array.from(groupedCommands.entries()).map(([category, cmds]) => (
                  <Fragment key={category}>
                    {search && (
                      <div className="px-3 py-2">
                        <p className="text-xs font-semibold text-khiimori-text-muted uppercase tracking-wider">
                          {category}
                        </p>
                      </div>
                    )}
                    {cmds.map((cmd, idx) => {
                      const globalIndex = filteredCommands.indexOf(cmd);
                      const isSelected = globalIndex === selectedIndex;

                      return (
                        <motion.button
                          key={cmd.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: idx * 0.02 }}
                          onClick={() => {
                            cmd.action();
                            onClose();
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                            isSelected
                              ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500'
                              : 'hover:bg-khiimori-bg-secondary hover:bg-khiimori-bg-elevated/50'
                          }`}
                        >
                          {/* Icon */}
                          <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded ${
                            isSelected ? 'bg-blue-100 dark:bg-blue-800' : 'bg-khiimori-bg-elevated'
                          }`}>
                            {cmd.icon || <Terminal className="w-4 h-4 text-khiimori-text-secondary" />}
                          </div>

                          {/* Label & Description */}
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-khiimori-text-primary text-khiimori-text-primary">
                              {cmd.label}
                            </p>
                            {cmd.description && (
                              <p className="text-xs text-khiimori-text-muted mt-0.5">
                                {cmd.description}
                              </p>
                            )}
                          </div>

                          {/* Shortcut */}
                          {cmd.shortcut && (
                            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-mono text-khiimori-text-muted bg-khiimori-bg-elevated rounded">
                              {cmd.shortcut}
                            </kbd>
                          )}
                        </motion.button>
                      );
                    })}
                  </Fragment>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-khiimori-bg-secondary  border-t border-khiimori-border">
            <div className="flex items-center justify-between text-xs text-khiimori-text-muted">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-khiimori-bg-elevated rounded">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-khiimori-bg-elevated rounded">↵</kbd>
                  Select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-khiimori-bg-elevated rounded">ESC</kbd>
                  Close
                </span>
              </div>
              <span>{filteredCommands.length} commands</span>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
